import api from './client'

export interface KeywordInput {
  term: string
  case_sensitive?: boolean
}

export interface SearchCreate {
  corpus_id?: string
  keywords: KeywordInput[]
  year_range?: number[]
  sections?: string[]
  article_types?: string[]
  entire_manuscript?: boolean
  include_non_oa?: boolean
  pmcid_restriction?: string[]
}

export interface SearchResultRow {
  pmcid: string
  pmid?: string
  title?: string
  text: string
  section?: string
  section_standard?: string
  keyword?: string
  matched_values?: string
  count?: number
  year?: number
  oa_status?: string
}

export interface SearchResponse {
  search_id: string
  user_id: string
  query_params: Record<string, any>
  result_count: number
  status: string
  created_at: string
}

export interface SearchResultResponse {
  search: SearchResponse
  results: SearchResultRow[]
  count: number
}

export interface PaginatedResultsResponse {
  results: SearchResultRow[]
  count: number
  page: number
  page_size: number
  total_pages: number
}

export interface ByArticleResultRow {
  pmcid: string
  pmid?: string
  title?: string
  year?: number
  oa_status?: string
  keywords: string[]
  matched_values: Record<string, number>
  match_count: number
}

export interface PaginatedByArticleResponse {
  results: ByArticleResultRow[]
  count: number
  page: number
  page_size: number
  total_pages: number
}

export interface IntersectionItem {
  keywords: string[]
  count: number
}

export interface ArticleSetMembership {
  pmcid: string
  pmid?: string
  sets: string[]
}

export interface AnalyticsRequest {
  exclude_keywords?: string[]
}

export interface SearchAnalyticsResponse {
  year_counts: Record<string, number>
  keyword_counts: Record<string, number>
  extracted_counts: Record<string, number>
  extracted_intersections: IntersectionItem[]
  article_sets: ArticleSetMembership[]
  total_articles: number
  total_matches: number
  all_keywords: string[]
}

export interface CooccurrenceFilter {
  keywords: string[]
  level: 'manuscript' | 'section' | 'sentence'
}

export interface PostSearchFilter {
  cooccurrence?: CooccurrenceFilter
  free_text_filters?: Record<string, string>
  year_range?: number[]
  keyword_min_counts?: Record<string, number>
}

export type ByArticleSortKey = 'pmcid' | 'title' | 'year' | 'match_count'
export type SortDir = 'asc' | 'desc'

export interface ArticleExtras {
  pmcid: string
  pmid: string | null
  abstract: string | null
  authors: string | null
  journal: string | null
}

export const searchApi = {
  getStats: (corpusId?: string) => api.get('/search/stats', { params: corpusId ? { corpus_id: corpusId } : undefined }),
  createSearch: (data: SearchCreate) => api.post<SearchResultResponse>('/search', data),
  getResults: (searchId: string, page: number = 1, pageSize: number = 50) =>
    api.get<PaginatedResultsResponse>(`/search/${searchId}/results`, { params: { page, page_size: pageSize } }),
  getResultsByArticle: (
    searchId: string,
    page: number = 1,
    pageSize: number = 50,
    sortBy: ByArticleSortKey = 'year',
    sortDir: SortDir = 'desc',
  ) =>
    api.get<PaginatedByArticleResponse>(
      `/search/${searchId}/results/by-article`,
      { params: { page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir } },
    ),
  filterResults: (searchId: string, data: PostSearchFilter, signal?: AbortSignal) =>
    api.post<SearchResultResponse>(`/search/${searchId}/filter`, data, { signal }),
  getFilteredResults: (searchId: string, page: number = 1, pageSize: number = 50) =>
    api.get<PaginatedResultsResponse>(`/search/${searchId}/filtered-results`, { params: { page, page_size: pageSize } }),
  getFilteredResultsByArticle: (
    searchId: string,
    page: number = 1,
    pageSize: number = 50,
    sortBy: ByArticleSortKey = 'year',
    sortDir: SortDir = 'desc',
  ) =>
    api.get<PaginatedByArticleResponse>(
      `/search/${searchId}/filtered-results/by-article`,
      { params: { page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir } },
    ),
  getAnalytics: (searchId: string, filtered?: boolean, request?: AnalyticsRequest) =>
    api.post<SearchAnalyticsResponse>(`/search/${searchId}/analytics`, request || {}, { params: { filtered } }),
  getRecentSearches: () => api.get('/search/my/recent'),
}

export const articleApi = {
  getExtras: (pmcid: string, corpusId?: string) =>
    api.get<ArticleExtras>(`/articles/${pmcid}`, { params: corpusId ? { corpus_id: corpusId } : undefined }),
}
